import { getAuthToken } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Comprehensive data sanitization utility to convert Firestore Timestamps and handle non-serializable data
 * @param {any} data - Data to sanitize
 * @returns {any} Sanitized data
 */
const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    const value = sanitized[key];
    
    // Check if value is a Firestore Timestamp
    if (value && typeof value === 'object' && value._seconds !== undefined && value._nanoseconds !== undefined) {
      // Convert Timestamp directly to ISO string for Redux serialization
      const date = new Date(value._seconds * 1000 + value._nanoseconds / 1000000);
      sanitized[key] = date.toISOString();
    } 
    // Handle Date objects - convert to ISO string for Redux serialization
    else if (value instanceof Date) {
      sanitized[key] = value.toISOString();
    }
    // Handle nested objects and arrays
    else if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'object' ? sanitizeData(item) : item
        );
      } else {
        sanitized[key] = sanitizeData(value);
      }
    }
  });
  
  return sanitized;
};

/**
 * Get flowsheets for an encounter
 * @param {string} encounterId - The encounter ID
 * @param {Object} options - Query options
 * @param {number} options.hours - Number of hours to look back
 * @returns {Promise<Array>} Array of flowsheet data
 */
export const getFlowsheets = async (encounterId, options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    
    // Ensure options is an object and handle null/undefined gracefully
    const safeOptions = options || {};
    
    if (safeOptions.hours) {
      queryParams.append('hours', safeOptions.hours.toString());
    }

    const response = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}/flowsheets?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching flowsheets:', error);
    throw error;
  }
};

/**
 * Get encounter by ID
 * @param {string} encounterId - The encounter ID
 * @returns {Promise<Object>} Encounter data
 */
export const getEncounter = async (encounterId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Extract encounter data and ensure id is properly set
    const encounter = data.encounter;
    if (!encounter) {
      throw new Error('Invalid response format: missing encounter data');
    }
    
    // Ensure the id field is set from the response
    if (!encounter.id) {
      encounter.id = encounterId;
    }
    
    // Apply comprehensive sanitization to convert all Firestore Timestamps
    return sanitizeData(encounter);
  } catch (error) {
    console.error('Error fetching encounter:', error);
    throw error;
  }
};

/**
 * Get encounter with all related data in a single API call
 * This is optimized to reduce multiple round-trips for encounter details page
 * Falls back to individual API calls if the aggregated endpoint is not available
 * @param {string} encounterId - The encounter ID
 * @returns {Promise<Object>} Object containing encounter, patient, prescriptions, and recentEncounters
 */
export const getEncounterFull = async (encounterId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}/full`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    // If the aggregated endpoint returns 404, fall back to individual fetch
    if (response.status === 404) {
      console.log('Aggregated endpoint not available, falling back to individual API calls');
      return await getEncounterFullFallback(encounterId, token);
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Ensure encounter has id field
    if (data.encounter && !data.encounter.id) {
      data.encounter.id = encounterId;
    }
    
    // Apply comprehensive sanitization to all data
    return {
      encounter: sanitizeData(data.encounter),
      patient: data.patient ? sanitizeData(data.patient) : null,
      prescriptions: Array.isArray(data.prescriptions) 
        ? data.prescriptions.map(p => sanitizeData(p)) 
        : [],
      recentEncounters: Array.isArray(data.recentEncounters) 
        ? data.recentEncounters.map(e => sanitizeData(e)) 
        : []
    };
  } catch (error) {
    console.error('Error fetching full encounter data:', error);
    // Try fallback on network errors as well
    try {
      console.log('Trying fallback due to error:', error.message);
      const token = await getAuthToken();
      return await getEncounterFullFallback(encounterId, token);
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
      throw error;
    }
  }
};

/**
 * Fallback function that fetches encounter data using individual API calls
 * @param {string} encounterId - The encounter ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Object containing encounter, patient, prescriptions, and recentEncounters
 */
const getEncounterFullFallback = async (encounterId, token) => {
  // First, fetch the encounter to get patientId
  const encounterResponse = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!encounterResponse.ok) {
    throw new Error(`HTTP error fetching encounter! status: ${encounterResponse.status}`);
  }

  const encounterData = await encounterResponse.json();
  const encounter = encounterData.encounter || encounterData;
  const patientId = encounter?.patientId;

  // Fetch patient, prescriptions, and recent encounters in parallel
  const [patientResult, prescriptionsResult, recentEncountersResult] = await Promise.allSettled([
    patientId ? fetch(`${API_BASE_URL}/clinical/facilities/${encounter.facilityId}/patients/${patientId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(r => r.ok ? r.json() : null) : Promise.resolve(null),
    
    patientId ? fetch(`${API_BASE_URL}/patients/${patientId}/prescriptions`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(r => r.ok ? r.json() : []) : Promise.resolve([]),
    
    patientId ? fetch(`${API_BASE_URL}/internal/patients/${patientId}/encounters/recent?limit=5`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(r => r.ok ? r.json() : []) : Promise.resolve([])
  ]);

  const patient = patientResult.status === 'fulfilled' ? patientResult.value : null;
  const prescriptions = prescriptionsResult.status === 'fulfilled' ? (prescriptionsResult.value?.prescriptions || prescriptionsResult.value || []) : [];
  const recentEncounters = recentEncountersResult.status === 'fulfilled' ? (recentEncountersResult.value?.encounters || recentEncountersResult.value || []) : [];

  return {
    encounter: sanitizeData({ id: encounterId, ...encounter }),
    patient: patient ? sanitizeData(patient) : null,
    prescriptions: Array.isArray(prescriptions) ? prescriptions.map(p => sanitizeData(p)) : [],
    recentEncounters: Array.isArray(recentEncounters) 
      ? recentEncounters.filter(e => e.id !== encounterId).map(e => sanitizeData(e)) 
      : []
  };
};

/**
 * Create a new encounter
 * @param {Object} encounterData - The encounter data
 * @returns {Promise<Object>} Created encounter data
 */
export const createEncounter = async (encounterData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/encounters`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(encounterData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Return the encounter data with the generated ID
    return {
      ...encounterData,
      id: data.id,
      docId: data.id
    };
  } catch (error) {
    console.error('Error creating encounter:', error);
    throw error;
  }
};

/**
 * Update an existing encounter
 * @param {string} encounterId - The encounter ID
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} Updated encounter data
 */
export const updateEncounter = async (encounterId, updateData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Return the updated encounter data with the encounter ID
    return {
      ...updateData,
      id: encounterId,
      encounterId: data.encounterId || encounterId
    };
  } catch (error) {
    console.error('Error updating encounter:', error);
    throw error;
  }
};

/**
 * Get patient encounters
 * @param {string} patientId - The patient ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of patient encounters
 */
export const getPatientEncounters = async (patientId, options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    
    // Ensure options is an object and handle null/undefined gracefully
    const safeOptions = options || {};
    
    if (safeOptions.status) {
      queryParams.append('status', safeOptions.status);
    }
    if (safeOptions.limit) {
      queryParams.append('limit', safeOptions.limit.toString());
    }
    if (safeOptions.facilityId) {
      queryParams.append('facilityId', safeOptions.facilityId);
    }

    // Use the clinical API endpoint instead of internal API for proper timestamp handling
    const response = await fetch(`${API_BASE_URL}/clinical/patients/${patientId}/encounters?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Return the raw response structure so Redux slice can handle extraction
    // This maintains consistency with how other encounter endpoints work
    return data;
  } catch (error) {
    console.error('Error fetching patient encounters:', error);
    throw error;
  }
};

/**
 * Get workflow tasks for an encounter
 * @param {string} encounterId - The encounter ID
 * @param {string} token - Authentication token
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of workflow tasks
 */
export const getWorkflowTasks = async (encounterId, token, options = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Ensure options is an object and handle null/undefined gracefully
    const safeOptions = options || {};
    
    if (safeOptions.status) {
      queryParams.append('status', safeOptions.status);
    }
    if (safeOptions.assignedTo) {
      queryParams.append('assignedTo', safeOptions.assignedTo);
    }

    const response = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}/workflow-tasks?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Apply comprehensive sanitization to convert all Firestore Timestamps
    return sanitizeData(data);
  } catch (error) {
    console.error('Error fetching workflow tasks:', error);
    throw error;
  }
};

/**
 * Update a workflow task
 * @param {string} encounterId - The encounter ID
 * @param {string} stepId - The workflow step ID
 * @param {Object} updateData - The data to update
 * @param {string} updatedBy - The user ID who made the update
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Updated workflow task data
 */
export const updateWorkflowTask = async (encounterId, stepId, updateData, updatedBy, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}/workflow-tasks/${stepId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...updateData, updatedBy })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Apply comprehensive sanitization to convert all Firestore Timestamps
    return sanitizeData(data);
  } catch (error) {
    console.error('Error updating workflow task:', error);
    throw error;
  }
};

/**
 * Get encounters for a specific facility
 * @param {string} facilityId - The facility ID
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by encounter status
 * @param {string} options.patientId - Filter by patient ID
 * @param {number} options.limit - Limit number of results
 * @param {boolean} options.summary - If true, returns only fields needed for list view (lightweight)
 * @returns {Promise<Object>} Object containing encounters array
 */
export const getFacilityEncounters = async (facilityId, options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    
    // Ensure options is an object and handle null/undefined gracefully
    const safeOptions = options || {};
    
    if (safeOptions.status) {
      queryParams.append('status', safeOptions.status);
    }
    if (safeOptions.patientId) {
      queryParams.append('patientId', safeOptions.patientId);
    }
    if (safeOptions.limit) {
      queryParams.append('limit', safeOptions.limit.toString());
    }
    if (safeOptions.summary) {
      queryParams.append('summary', 'true');
    }

    const url = `${API_BASE_URL}/internal/encounters?${queryParams}&facilityId=${facilityId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Return the raw response structure so Redux slice can handle extraction
    // This maintains consistency with how other encounter endpoints work
    return data;
  } catch (error) {
    console.error('Error fetching facility encounters:', error);
    throw error;
  }
};

/**
 * Get all encounters
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by encounter status
 * @param {string} options.facilityId - Filter by facility ID
 * @param {number} options.limit - Limit number of results
 * @param {number} options.offset - Offset for pagination
 * @returns {Promise<Array>} Array of encounters
 */
export const getAllEncounters = async (options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    
    // Ensure options is an object and handle null/undefined gracefully
    const safeOptions = options || {};
    
    if (safeOptions.status) {
      queryParams.append('status', safeOptions.status);
    }
    if (safeOptions.facilityId) {
      queryParams.append('facilityId', safeOptions.facilityId);
    }
    if (safeOptions.limit) {
      queryParams.append('limit', safeOptions.limit.toString());
    }
    if (safeOptions.offset) {
      queryParams.append('offset', safeOptions.offset.toString());
    }

    const response = await fetch(`${API_BASE_URL}/internal/encounters?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Return the raw response structure so Redux slice can handle extraction
    // This maintains consistency with how other encounter endpoints work
    return data;
  } catch (error) {
    console.error('Error fetching all encounters:', error);
    throw error;
  }
};

/**
 * Get recent encounters
 * @param {Object} options - Query options
 * @param {number} options.hours - Number of hours to look back
 * @param {string} options.facilityId - Filter by facility ID
 * @param {number} options.limit - Limit number of results
 * @returns {Promise<Array>} Array of recent encounters
 */
export const getRecentEncounters = async (options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    
    // Ensure options is an object and handle null/undefined gracefully
    const safeOptions = options || {};
    
    if (safeOptions.hours) {
      queryParams.append('hours', safeOptions.hours.toString());
    }
    if (safeOptions.facilityId) {
      queryParams.append('facilityId', safeOptions.facilityId);
    }
    if (safeOptions.limit) {
      queryParams.append('limit', safeOptions.limit.toString());
    }

    const response = await fetch(`${API_BASE_URL}/internal/encounters/recent?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Return the raw response structure so Redux slice can handle extraction
    // This maintains consistency with how other encounter endpoints work
    return data;
  } catch (error) {
    console.error('Error fetching recent encounters:', error);
    throw error;
  }
};

/**
 * Get encounter by ID (alias for getEncounter)
 * @param {string} encounterId - The encounter ID
 * @returns {Promise<Object>} Encounter data
 */
export const getEncounterById = async (encounterId) => {
  return getEncounter(encounterId);
};



/**
 * Update encounter location information
 * @param {string} encounterId - The encounter ID
 * @param {Object} locationData - The location data to update
 * @param {string} locationData.departmentId - Department ID
 * @param {string} locationData.roomId - Room ID
 * @param {string} locationData.bedId - Bed ID (optional)
 * @param {string} locationData.facilityId - Facility ID
 * @returns {Promise<Object>} Updated encounter data
 */
export const updateEncounterLocation = async (encounterId, locationData) => {
  try {
    const token = await getAuthToken();
    
    // Use the dedicated location endpoint that ensures bed assignments are updated
    const response = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}/location`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        departmentId: locationData.departmentId,
        roomId: locationData.roomId,
        bedId: locationData.bedId || null,
        facilityId: locationData.facilityId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating encounter location:', error);
    throw error;
  }
}; 

/**
 * Clear encounter-related caches to prevent stale data
 * This should be called when switching between encounters or patients
 * @param {string} patientId - The patient ID to clear cache for
 * @param {string} encounterId - The encounter ID to clear cache for (optional)
 */
export const clearEncounterCache = async (patientId, encounterId = null) => {
  try {
    console.log(`Clearing encounter cache for patient ${patientId}${encounterId ? ` and encounter ${encounterId}` : ''}`);
    
    // Clear any browser-level caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      const encounterCaches = cacheNames.filter(name => 
        name.includes('encounter') || name.includes('procedure') || name.includes('patient')
      );
      
      await Promise.all(encounterCaches.map(name => caches.delete(name)));
      console.log(`Cleared ${encounterCaches.length} browser caches`);
    }
    
    // Clear any in-memory caches by dispatching a custom event
    window.dispatchEvent(new CustomEvent('clearEncounterCache', {
      detail: { patientId, encounterId }
    }));
    
    return true;
  } catch (error) {
    console.error('Error clearing encounter cache:', error);
    return false;
  }
};

/**
 * Trigger AI insights generation for a specific encounter
 * @param {string} encounterId - The encounter ID to generate AI insights for
 * @param {string} patientId - The patient ID
 * @returns {Promise<Object>} Response indicating AI insights generation has started
 */
export const triggerAIInsights = async (encounterId, patientId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/encounters/${encounterId}/ai-insights`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        triggerAI: true,
        patientId: patientId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('AI insights generation triggered successfully');
    return result;
  } catch (error) {
    console.error('Error triggering AI insights:', error);
    throw error;
  }
}; 